/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, computed, signal,
} from '@angular/core';
import { CommunityProfileActivityKind } from './activity-card/community-profile-activity';
import { POSTS_SEGMENTS } from './posts/community-profile-posts.component';

export type ProfileSection = 'overview' | 'posts' | CommunityProfileActivityKind | 'people';
export type PostsSegment = 'articles' | 'status' | 'topics';
export type PeopleKind = 'followers' | 'following';

const SECTION_BY_STATE: Record<string, ProfileSection> = {
  'profile.about': 'overview',
  'profile.articles': 'posts',
  'profile.status': 'posts',
  'profile.topics': 'posts',
  'profile.comments': 'comments',
  'profile.mentions': 'mentions',
  'profile.likes': 'likes',
  'profile.followers': 'people',
  'profile.following': 'people',
};

// The /profile/:profileId page in the 2026 design, routed by UTEST_DESIGN_REFRESH_2026 (see PROFILE_REDESIGN.html).
// The header stays put while the existing child states pick what's below it, so every old URL still works.
@Component({
  selector: 'ut-community-profile',
  templateUrl: './community-profile.component.html',
  styleUrls: ['./community-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfileComponent implements OnInit, OnDestroy {
  // The profile being viewed, from the `profile` state's resolve.
  @Input({ required: true }) user: any;

  // The logged-in user: undefined while loading, null when logged out.
  me = signal<any>(undefined);
  testerAppUrl = signal('');
  utestUrl = signal('');
  stateName = signal('');

  section = computed(() => SECTION_BY_STATE[this.stateName()] || 'overview');
  // The state name minus "profile.": which post type, or which People list, is open.
  child = computed(() => this.stateName().replace('profile.', ''));
  readonly postTabs = POSTS_SEGMENTS;
  // The same sections for the narrow-screen "Show" picker, keyed by state name.
  views = computed(() => [
    { value: 'profile.about', label: 'Overview' },
    ...POSTS_SEGMENTS.map((tab) => ({ value: tab.state, label: tab.label })),
    { value: 'profile.comments', label: 'Comments' },
    { value: 'profile.mentions', label: 'Mentions' },
    ...(this.isOwn() ? [{ value: 'profile.likes', label: 'Likes' }] : []),
  ]);

  isOwn = computed(() => !!this.me() && this.me().id === this.user.id);
  // Heads a list tab for screen readers, so the cards' headings (h3) sit under an h2.
  sectionTitle = computed(() => {
    const people = { 'profile.followers': 'Followers', 'profile.following': 'Following' }[this.stateName()];
    return people || this.views().find((v) => v.value === this.stateName())?.label || '';
  });

  // The profile's own count is loaded once with the state, so follows on its Followers / Following
  // lists adjust it here (only your own count changes when you follow someone).
  followingCount = signal(0);

  private removeTransitionHook: () => void;

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('ConfigService') private ConfigService: any,
    @Inject('$state') private $state: any,
    @Inject('$transitions') private $transitions: any,
  ) {}

  ngOnInit(): void {
    this.followingCount.set(this.user.follow_stats?.following_count ?? 0);
    this.stateName.set(this.$state.current.name);
    this.removeTransitionHook = this.$transitions.onSuccess({ to: 'profile.**' }, (transition) => {
      this.stateName.set(transition.to().name);
      this.guardLikes();
    });

    this.UserService.me().then((res) => {
      this.me.set(res?.data || null);
      this.guardLikes();
    }, () => this.me.set(null));
    this.ConfigService.config().then((res) => {
      this.testerAppUrl.set(res.data.profile_url);
      this.utestUrl.set(res.data.utest_url);
    });
  }

  onFollowChange(following: boolean): void {
    if (this.isOwn()) this.followingCount.update((n) => Math.max(0, n + (following ? 1 : -1)));
  }

  go(state: string): void {
    this.$state.go(state, { profileId: this.user.slug });
  }

  ngOnDestroy(): void {
    this.removeTransitionHook();
  }

  // Likes come from /users/me/likes, so the tab only makes sense on your own profile.
  private guardLikes(): void {
    if (this.me() !== undefined && this.section() === 'likes' && !this.isOwn()) {
      this.$state.go('profile.about', { profileId: this.user.slug }, { location: 'replace' });
    }
  }
}
